import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import useSeo from '@/hooks/useSeo';
import { filterArticles, type RangeKey } from '@/utils/data';
import { highlightText, relativeTime, formatViews } from '@/utils/format';
import { hotSearches } from '@/mocks/site';

const tabs = [
  { key: 'all', label: '全部分类' },
  { key: 'hot-news', label: '热点新闻' },
  { key: 'expose', label: '爆料报道' },
  { key: 'web-fun', label: '网络趣闻' },
];

const ranges: { key: RangeKey; label: string }[] = [
  { key: 'all', label: '全部时间' },
  { key: 'today', label: '今天' },
  { key: '3d', label: '近三天' },
  { key: '7d', label: '近一周' },
];

function Highlighted({ text, keyword }: { text: string; keyword: string }) {
  const parts = highlightText(text, keyword);
  return (
    <>
      {parts.map((p, i) =>
        p.match ? (
          <mark key={i} className="bg-primary-200 text-primary-900 rounded px-0.5">
            {p.text}
          </mark>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </>
  );
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const category = searchParams.get('cat') || 'all';
  const range = (searchParams.get('range') as RangeKey) || 'all';
  const [input, setInput] = useState(q);

  useEffect(() => {
    setInput(q);
  }, [q]);

  const results = useMemo(
    () => (q ? filterArticles({ keyword: q, category: category === 'all' ? undefined : category, range, sort: 'latest' }) : []),
    [q, category, range],
  );

  useSeo({
    title: q ? `“${q}” 搜索结果 - 51吃瓜网` : '全站搜索 - 51吃瓜网',
    description: q
      ? `51吃瓜网关于“${q}”的搜索结果，共 ${results.length} 条相关娱乐资讯。`
      : '在51吃瓜网搜索热点新闻、爆料报道与网络趣闻。',
    keywords: `${q},51吃瓜网搜索,娱乐资讯`,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (input.trim()) next.set('q', input.trim());
    else next.delete('q');
    setSearchParams(next);
  };

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <Breadcrumb items={[{ label: '搜索' }]} />

        <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8 mb-6">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            {q ? (
              <>
                搜索结果：<span className="text-primary-600">“{q}”</span>
              </>
            ) : (
              '全站搜索'
            )}
          </h1>
          <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line" />
              </span>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入关键词，如：红毯、综艺、热梗"
                className="w-full pl-11 pr-4 py-3 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <span className="text-xs text-foreground-400">热门搜索：</span>
            {hotSearches.map((h) => (
              <Link
                key={h}
                to={`/search?q=${encodeURIComponent(h)}`}
                className="px-3 py-1 rounded-full bg-background-50 border border-background-200 text-xs text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors"
              >
                {h}
              </Link>
            ))}
          </div>
        </div>

        {q && (
          <div className="space-y-4 mb-6">
            <div className="flex flex-wrap items-center gap-2">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setParam('cat', t.key)}
                  className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    category === t.key ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100">
              {ranges.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setParam('range', r.key)}
                  className={`px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    range === r.key ? 'bg-background-50 text-primary-600 font-medium' : 'text-foreground-600 hover:text-primary-600'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <p className="text-sm text-foreground-400">共找到 {results.length} 条相关资讯</p>
          </div>
        )}

        {q && results.length > 0 && (
          <div className="space-y-4 max-w-4xl">
            {results.map((item) => (
              <Link
                key={item.id}
                to={`/detail/${item.id}`}
                className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
              >
                <div className="w-full sm:w-44 h-44 sm:h-28 shrink-0 rounded-md overflow-hidden bg-background-200">
                  <img src={item.cover} alt={item.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="inline-block px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs mb-2">{item.categoryName}</span>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    <Highlighted text={item.title} keyword={q} />
                  </h3>
                  <p className="mt-1.5 text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                    <Highlighted text={item.summary} keyword={q} />
                  </p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
                    <span className="flex items-center gap-1"><i className="ri-time-line" /> {relativeTime(item.publishTime)}</span>
                    <span className="flex items-center gap-1"><i className="ri-eye-line" /> {formatViews(item.views)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {q && results.length === 0 && (
          <div className="py-20 text-center rounded-lg bg-background-100 border border-background-200">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-50 text-foreground-400">
              <i className="ri-search-eye-line text-3xl" />
            </div>
            <p className="mt-5 font-heading text-lg font-semibold text-foreground-900">未找到相关资讯，换个关键词试试吧！</p>
            <p className="mt-2 text-sm text-foreground-500">也可以试试下面的热门搜索：</p>
            <div className="flex flex-wrap justify-center gap-2 mt-5 max-w-xl mx-auto">
              {hotSearches.map((h) => (
                <Link
                  key={h}
                  to={`/search?q=${encodeURIComponent(h)}`}
                  className="px-3.5 py-1.5 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors"
                >
                  {h}
                </Link>
              ))}
            </div>
          </div>
        )}

        {!q && (
          <div className="py-16 text-center rounded-lg bg-background-50 border border-dashed border-background-300">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-line text-3xl" />
            </div>
            <p className="mt-5 font-heading text-lg font-semibold text-foreground-900">输入关键词，开始吃瓜</p>
            <p className="mt-2 text-sm text-foreground-500">支持搜索标题、摘要与话题标签（前端演示搜索）</p>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}